import { useMemo, useState } from "react";

import { BarChart3, Check, Hash, Plus, Table2, X } from "lucide-react";

import {
  CHART_TYPE_LABEL,
  VALUE_FORMAT_LABEL,
  type ChartType,
  type TrendSpec,
  type ValueFormat,
} from "@/components/cinch/charts";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";

import { type ReportPanel } from "../api";
import {
  DRILLDOWN_TARGETS,
  inferVizSpec,
  type AgentQueryResult,
  type PanelVizSpec,
} from "../viz";
import { PanelChart } from "./PanelChart";
import { StatView } from "./StatView";

const CHART_TYPES: ChartType[] = [
  "line",
  "area",
  "stacked-area",
  "bar",
  "horizontal-bar",
  "stacked-bar",
  "scatter",
  "bubble",
  "pie",
  "donut",
];
const VALUE_FORMATS = Object.keys(VALUE_FORMAT_LABEL) as ValueFormat[];

/**
 * Configure a panel from a query result: title, chart type, which column is x,
 * which are series, value format — with a live preview — then add it to (or,
 * when {@link editing}, update it in) the report. The component is remounted per
 * query (keyed on the SQL) so its controls start fresh; when editing a saved
 * panel the parent passes {@link initialViz}/{@link initialTitle} so the controls
 * start from that panel's spec instead of a fresh inference.
 */
export function PanelEditor({
  result,
  onAdd,
  saving,
  editing,
  initialViz,
  initialTitle,
  initialMode,
}: {
  result: AgentQueryResult;
  onAdd: (panel: ReportPanel) => void;
  saving?: boolean;
  /** Editing an existing panel (updates in place) vs. adding a new one. */
  editing?: boolean;
  /** When editing, pre-fill the controls from the panel's saved spec. */
  initialViz?: PanelVizSpec;
  initialTitle?: string | null;
  initialMode?: "chart" | "table" | "stat";
}) {
  const inferred = useMemo(() => inferVizSpec(result), [result]);

  const [title, setTitle] = useState(initialTitle ?? "");
  const [mode, setMode] = useState<"chart" | "table" | "stat">(initialMode ?? "chart");
  const [chartType, setChartType] = useState<ChartType>(
    initialViz?.chartType ?? inferred.chartType ?? "bar",
  );
  const [xKey, setXKey] = useState<string>(
    initialViz?.xKey ?? inferred.xKey ?? result.columns[0] ?? "",
  );
  const [seriesSel, setSeriesSel] = useState<string[]>(
    (initialViz?.series?.length ? initialViz.series : (inferred.series ?? [])).map((s) => s.key),
  );
  const [valueFormat, setValueFormat] = useState<ValueFormat>(
    initialViz?.valueFormat ?? inferred.valueFormat ?? "count",
  );
  const [drilldownOn, setDrilldownOn] = useState(!!initialViz?.drilldown);
  const [drillRoute, setDrillRoute] = useState<string>(
    initialViz?.drilldown?.route ?? DRILLDOWN_TARGETS[0]?.route ?? "/contracts",
  );
  const [drillParams, setDrillParams] = useState<{ key: string; col: string }[]>(
    initialViz?.drilldown
      ? Object.entries(initialViz.drilldown.params ?? {}).map(([key, tmpl]) => ({
          key,
          col: String(tmpl).replace(/[{}]/g, ""),
        }))
      : [],
  );
  const [trend, setTrend] = useState<TrendSpec | null>(initialViz?.trend ?? null);
  const [statKey, setStatKey] = useState<string>(
    initialViz?.valueKey ??
      result.columns.find((c) => {
        const v = result.rows[0]?.[c];
        return v != null && v !== "" && Number.isFinite(Number(v));
      }) ??
      result.columns[0] ??
      "",
  );
  // Per-series chart-type overrides for combination charts (bars + a line, …).
  const [seriesTypes, setSeriesTypes] = useState<Record<string, "line" | "area" | "bar">>(() =>
    Object.fromEntries(
      (initialViz?.series ?? [])
        .filter((s) => s.type)
        .map((s) => [s.key, s.type as "line" | "area" | "bar"]),
    ),
  );
  // Split-by: pivot a category column into one series per value (long → wide).
  const [splitBy, setSplitBy] = useState<string>(initialViz?.splitBy ?? "");
  const [splitValue, setSplitValue] = useState<string>(
    initialViz?.valueKey ??
      result.columns.find((c) => {
        const v = result.rows[0]?.[c];
        return v != null && v !== "" && Number.isFinite(Number(v));
      }) ??
      "",
  );

  const seriesKeys = seriesSel.filter((k) => k !== xKey);
  const supportsSplit =
    chartType !== "pie" && chartType !== "donut" && chartType !== "scatter" && chartType !== "bubble";
  const useSplit = supportsSplit && !!splitBy;
  const drilldown =
    drilldownOn && drillRoute
      ? {
          route: drillRoute,
          params: Object.fromEntries(
            drillParams
              .filter((p) => p.key.trim() && p.col)
              .map((p) => [p.key.trim(), `{${p.col}}`]),
          ),
        }
      : undefined;
  const viz: PanelVizSpec = useSplit
    ? {
        chartType,
        xKey,
        splitBy,
        valueKey: splitValue,
        valueFormat,
        legend: true,
        kind: inferred.kind,
        ...(drilldown ? { drilldown } : {}),
        ...(trend ? { trend } : {}),
      }
    : {
        chartType,
        xKey,
        series: seriesKeys.map((k) => ({
          key: k,
          ...(seriesTypes[k] ? { type: seriesTypes[k] } : {}),
        })),
        valueFormat,
        legend: seriesKeys.length > 1,
        kind: inferred.kind,
        ...(drilldown ? { drilldown } : {}),
        ...(trend ? { trend } : {}),
      };

  // Trend-line control ↔ TrendSpec (a flat select over the type + reference mode).
  const trendChoice = !trend
    ? "none"
    : trend.type === "reference"
      ? trend.mode === "value"
        ? "reference-value"
        : "reference-average"
      : trend.type;
  const onTrendChange = (v: string) =>
    setTrend(
      v === "linear"
        ? { type: "linear" }
        : v === "moving-average"
          ? { type: "moving-average", window: 3 }
          : v === "reference-average"
            ? { type: "reference", mode: "average" }
            : v === "reference-value"
              ? { type: "reference", mode: "value", value: 0 }
              : null,
    );

  const chartReady = useSplit ? !!splitValue : seriesKeys.length > 0;
  const canAdd = mode === "table" || mode === "stat" || chartReady;

  const toggleSeries = (col: string) =>
    setSeriesSel((prev) =>
      prev.includes(col) ? prev.filter((c) => c !== col) : [...prev, col],
    );

  const addParam = () =>
    setDrillParams((p) => [...p, { key: "", col: result.columns[0] ?? "" }]);
  const updateParam = (i: number, patch: Partial<{ key: string; col: string }>) =>
    setDrillParams((p) => p.map((row, idx) => (idx === i ? { ...row, ...patch } : row)));
  const removeParam = (i: number) =>
    setDrillParams((p) => p.filter((_, idx) => idx !== i));

  const add = () => {
    const panel: ReportPanel = {
      id: crypto.randomUUID(),
      type: mode === "table" ? "grid" : mode === "stat" ? "stat" : "chart",
      title: title.trim() || null,
      layout: { x: 0, y: 0, w: 6, h: 8 },
      query: result.sql ? { source: result.source ?? "snowflake", sql: result.sql } : null,
      viz:
        mode === "table"
          ? { columns: result.columns }
          : mode === "stat"
            ? { valueKey: statKey, valueFormat }
            : viz,
    };
    onAdd(panel);
    setTitle("");
  };

  return (
    <div className="space-y-3">
      {/* Chart/Table + chart-type toggles */}
      <div className="flex flex-wrap items-center gap-1.5">
        <div className="mr-auto inline-flex overflow-hidden rounded-md border border-border">
          <Toggle active={mode === "chart"} onClick={() => setMode("chart")}>
            <BarChart3 className="h-3.5 w-3.5" /> Chart
          </Toggle>
          <Toggle active={mode === "table"} onClick={() => setMode("table")}>
            <Table2 className="h-3.5 w-3.5" /> Table
          </Toggle>
          <Toggle active={mode === "stat"} onClick={() => setMode("stat")}>
            <Hash className="h-3.5 w-3.5" /> Stat
          </Toggle>
        </div>
        {mode === "chart" &&
          CHART_TYPES.map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setChartType(t)}
              className={cn(
                "rounded-md border px-2 py-1 text-[11px] font-medium transition-colors",
                t === chartType
                  ? "border-brand bg-brand text-white"
                  : "border-border text-muted-foreground hover:bg-muted",
              )}
            >
              {CHART_TYPE_LABEL[t]}
            </button>
          ))}
      </div>

      {mode === "stat" ? (
        <StatView result={result} viz={{ valueKey: statKey, valueFormat }} className="h-64" />
      ) : (
        <PanelChart result={result} viz={viz} mode={mode} className="h-64" />
      )}

      {/* Column mapping + drill-down (chart mode only) */}
      {mode === "chart" && (
        <div className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1 text-xs">
            <span className="font-medium text-muted-foreground">X axis / category</span>
            <Select value={xKey} onValueChange={setXKey}>
              <SelectTrigger className="h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {result.columns.map((c) => (
                  <SelectItem key={c} value={c}>
                    {c}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1 text-xs">
            <span className="font-medium text-muted-foreground">Value format</span>
            <Select value={valueFormat} onValueChange={(v) => setValueFormat(v as ValueFormat)}>
              <SelectTrigger className="h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {VALUE_FORMATS.map((f) => (
                  <SelectItem key={f} value={f}>
                    {VALUE_FORMAT_LABEL[f]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {supportsSplit && (
            <div className="space-y-1 text-xs">
              <span className="font-medium text-muted-foreground">Split by (series)</span>
              <Select
                value={splitBy || "__none"}
                onValueChange={(v) => setSplitBy(v === "__none" ? "" : v)}
              >
                <SelectTrigger className="h-8">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none">None</SelectItem>
                  {result.columns
                    .filter((c) => c !== xKey)
                    .map((c) => (
                      <SelectItem key={c} value={c}>
                        {c}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
          )}
          {useSplit ? (
            <div className="space-y-1 text-xs">
              <span className="font-medium text-muted-foreground">Value column</span>
              <Select value={splitValue} onValueChange={setSplitValue}>
                <SelectTrigger className="h-8">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {result.columns
                    .filter((c) => c !== xKey && c !== splitBy)
                    .map((c) => (
                      <SelectItem key={c} value={c}>
                        {c}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
          ) : (
            <div className="space-y-1 text-xs sm:col-span-2">
              <span className="font-medium text-muted-foreground">Series (Y)</span>
              <div className="flex flex-wrap gap-x-4 gap-y-1.5">
                {result.columns
                  .filter((c) => c !== xKey)
                  .map((c) => (
                    <label key={c} className="flex items-center gap-1.5">
                      <Checkbox
                        checked={seriesSel.includes(c)}
                        onCheckedChange={() => toggleSeries(c)}
                      />
                      <span>{c}</span>
                    </label>
                  ))}
              </div>
            </div>
          )}
          </div>

          {/* Combination — mix chart types per series (bars + a line, …) */}
          {!useSplit &&
            (chartType === "line" || chartType === "area" || chartType === "bar") &&
            seriesKeys.length >= 2 && (
              <div className="space-y-1.5 border-t border-border pt-3 text-xs">
                <span className="font-medium text-muted-foreground">Series type (combination)</span>
                {seriesKeys.map((k) => (
                  <div key={k} className="flex items-center gap-2">
                    <span className="w-32 shrink-0 truncate">{k}</span>
                    <Select
                      value={seriesTypes[k] ?? "default"}
                      onValueChange={(v) =>
                        setSeriesTypes((prev) => {
                          const next = { ...prev };
                          if (v === "default") delete next[k];
                          else next[k] = v as "line" | "area" | "bar";
                          return next;
                        })
                      }
                    >
                      <SelectTrigger className="h-8 flex-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="default">
                          Default ({CHART_TYPE_LABEL[chartType]})
                        </SelectItem>
                        <SelectItem value="line">Line</SelectItem>
                        <SelectItem value="bar">Bar</SelectItem>
                        <SelectItem value="area">Area</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
            )}

          {/* Trend line (optional) — computed overlay (AG has no built-in one) */}
          <div className="grid gap-3 border-t border-border pt-3 sm:grid-cols-2">
            <div className="space-y-1 text-xs">
              <span className="font-medium text-muted-foreground">Trend line</span>
              <Select value={trendChoice} onValueChange={onTrendChange}>
                <SelectTrigger className="h-8">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  <SelectItem value="linear">Linear regression</SelectItem>
                  <SelectItem value="moving-average">Moving average</SelectItem>
                  <SelectItem value="reference-average">Reference: average</SelectItem>
                  <SelectItem value="reference-value">Reference: value</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {trend?.type === "moving-average" && (
              <div className="space-y-1 text-xs">
                <span className="font-medium text-muted-foreground">Window (points)</span>
                <Input
                  type="number"
                  min={2}
                  className="h-8"
                  value={trend.window ?? 3}
                  onChange={(e) =>
                    setTrend({
                      type: "moving-average",
                      window: Math.max(2, Number(e.target.value) || 3),
                    })
                  }
                />
              </div>
            )}
            {trend?.type === "reference" && trend.mode === "value" && (
              <div className="space-y-1 text-xs">
                <span className="font-medium text-muted-foreground">Value</span>
                <Input
                  type="number"
                  className="h-8"
                  value={trend.value ?? 0}
                  onChange={(e) =>
                    setTrend({ type: "reference", mode: "value", value: Number(e.target.value) || 0 })
                  }
                />
              </div>
            )}
          </div>

          {/* Drill-down (optional) — make the chart clickable */}
          <div className="space-y-2 border-t border-border pt-3">
            <label className="flex items-center gap-2 text-xs">
              <Checkbox
                checked={drilldownOn}
                onCheckedChange={(v) => setDrilldownOn(v === true)}
              />
              <span className="font-medium">Make clickable — drill into a Cinch Ops page</span>
            </label>
            {drilldownOn && (
              <div className="space-y-2 pl-6">
                <div className="space-y-1 text-xs">
                  <span className="font-medium text-muted-foreground">Open</span>
                  <Select value={drillRoute} onValueChange={setDrillRoute}>
                    <SelectTrigger className="h-8">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {DRILLDOWN_TARGETS.map((t) => (
                        <SelectItem key={t.route} value={t.route}>
                          {t.label} · {t.route}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5 text-xs">
                  <span className="font-medium text-muted-foreground">
                    Pass parameters (URL param ← clicked row’s column)
                  </span>
                  {drillParams.map((p, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <Input
                        className="h-8 w-28"
                        placeholder="param"
                        value={p.key}
                        onChange={(e) => updateParam(i, { key: e.target.value })}
                      />
                      <span className="text-muted-foreground">←</span>
                      <Select value={p.col} onValueChange={(v) => updateParam(i, { col: v })}>
                        <SelectTrigger className="h-8 flex-1">
                          <SelectValue placeholder="column" />
                        </SelectTrigger>
                        <SelectContent>
                          {result.columns.map((c) => (
                            <SelectItem key={c} value={c}>
                              {c}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <button
                        type="button"
                        onClick={() => removeParam(i)}
                        className="text-muted-foreground hover:text-danger"
                        title="Remove parameter"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                  <Button variant="outline" size="sm" onClick={addParam}>
                    <Plus className="h-3.5 w-3.5" />
                    Add parameter
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Stat mode: value column + format */}
      {mode === "stat" && (
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1 text-xs">
            <span className="font-medium text-muted-foreground">Value column</span>
            <Select value={statKey} onValueChange={setStatKey}>
              <SelectTrigger className="h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {result.columns.map((c) => (
                  <SelectItem key={c} value={c}>
                    {c}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1 text-xs">
            <span className="font-medium text-muted-foreground">Value format</span>
            <Select value={valueFormat} onValueChange={(v) => setValueFormat(v as ValueFormat)}>
              <SelectTrigger className="h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {VALUE_FORMATS.map((f) => (
                  <SelectItem key={f} value={f}>
                    {VALUE_FORMAT_LABEL[f]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      )}

      {/* Title + add */}
      <div className="flex items-end gap-2 border-t border-border pt-3">
        <div className="flex-1 space-y-1 text-xs">
          <span className="font-medium text-muted-foreground">Panel title</span>
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. Monthly recurring revenue"
            className="h-8"
          />
        </div>
        <Button size="sm" onClick={add} disabled={!canAdd || saving}>
          {editing ? (
            <>
              <Check className="h-4 w-4" />
              Save changes
            </>
          ) : (
            <>
              <Plus className="h-4 w-4" />
              Add to report
            </>
          )}
        </Button>
      </div>
    </div>
  );
}

function Toggle({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium transition-colors",
        active ? "bg-brand text-white" : "bg-transparent text-muted-foreground hover:bg-muted",
      )}
    >
      {children}
    </button>
  );
}
